// src/components/wizard/StepResult.jsx - ステップ2: 結果確認・編集

import React, { useState, useEffect, useRef } from 'react';
import { COLORS, SPACING, BORDER_RADIUS, TRANSITIONS } from '../../styles/constants';

const StepResult = ({
  generatedPost,
  editedPost,
  setEditedPost,
  isEditing,
  setIsEditing,
  onUsePost,
  onRegenerate,
  showToast
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const textareaRef = useRef(null);

  // タイピングアニメーション
  useEffect(() => {
    if (!generatedPost) return;

    setDisplayedText('');
    setIsTyping(true);

    let index = 0;
    const interval = setInterval(() => {
      if (index < generatedPost.length) {
        setDisplayedText(generatedPost.slice(0, index + 1));
        index++;
      } else {
        setIsTyping(false);
        clearInterval(interval);
      }
    }, 20);

    return () => clearInterval(interval);
  }, [generatedPost]);

  const handleCopy = async () => {
    const textToCopy = isEditing ? editedPost : generatedPost;
    try {
      await navigator.clipboard.writeText(textToCopy);
      showToast('クリップボードにコピーしました', 'success');
    } catch (err) {
      showToast('コピーに失敗しました', 'error');
    }
  };

  const skipTyping = () => {
    setDisplayedText(generatedPost);
    setIsTyping(false);
  };

  const characterCount = (isEditing ? editedPost : generatedPost).length;

  return (
    <div style={{
      background: COLORS.surface,
      borderRadius: BORDER_RADIUS.xl,
      padding: SPACING.xl,
      border: `1px solid ${COLORS.border}`,
      animation: 'fadeIn 0.3s ease-out'
    }}>
      {/* ヘッダー */}
      <div style={{
        textAlign: 'center',
        marginBottom: SPACING.xl
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(52, 211, 153, 0.2))',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
          marginBottom: SPACING.md,
          fontSize: '1.75rem'
        }}>
          ✅
        </div>
        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: '#fff',
          margin: 0,
          marginBottom: SPACING.xs
        }}>
          投稿文が完成しました！
        </h2>
        <p style={{
          fontSize: '0.875rem',
          color: '#64748b',
          margin: 0
        }}>
          内容を確認して、必要に応じて編集できます
        </p>
      </div>

      {/* 生成結果表示/編集エリア */}
      <div style={{
        marginBottom: SPACING.lg
      }}>
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={editedPost}
            onChange={(e) => setEditedPost(e.target.value)}
            style={{
              width: '100%',
              minHeight: '200px',
              padding: SPACING.md,
              border: `1px solid ${COLORS.primary}`,
              borderRadius: BORDER_RADIUS.md,
              fontSize: '1rem',
              lineHeight: '1.6',
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
              background: COLORS.background,
              color: '#e2e8f0'
            }}
            autoFocus
          />
        ) : (
          <div
            onClick={skipTyping}
            style={{
              background: COLORS.background,
              borderRadius: BORDER_RADIUS.md,
              padding: SPACING.lg,
              minHeight: '200px',
              cursor: isTyping ? 'pointer' : 'default',
              position: 'relative',
              border: `1px solid ${COLORS.border}`
            }}
          >
            <div style={{
              fontSize: '1rem',
              lineHeight: '1.8',
              color: '#e2e8f0',
              whiteSpace: 'pre-wrap'
            }}>
              {displayedText}
              {isTyping && (
                <span style={{
                  display: 'inline-block',
                  width: '2px',
                  height: '1.2em',
                  background: COLORS.primary,
                  marginLeft: '2px',
                  animation: 'blink 0.8s infinite'
                }} />
              )}
            </div>
            {isTyping && (
              <div style={{
                position: 'absolute',
                bottom: SPACING.sm,
                right: SPACING.sm,
                fontSize: '0.75rem',
                color: '#64748b'
              }}>
                タップでスキップ
              </div>
            )}
          </div>
        )}

        {/* 文字数カウント */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: SPACING.sm
        }}>
          <span style={{
            fontSize: '0.75rem',
            color: characterCount > 280 ? COLORS.error : '#64748b'
          }}>
            {characterCount}/280文字
            {characterCount > 280 && ' (Xの制限を超えています)'}
          </span>
          <div style={{
            display: 'flex',
            gap: SPACING.sm
          }}>
            <button
              onClick={handleCopy}
              style={{
                padding: '0.375rem 0.75rem',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: BORDER_RADIUS.sm,
                cursor: 'pointer',
                fontSize: '0.75rem',
                color: '#94a3b8',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}
            >
              📋 コピー
            </button>
            <button
              onClick={() => {
                if (isEditing) {
                  setIsEditing(false);
                } else {
                  setEditedPost(generatedPost);
                  setIsEditing(true);
                }
              }}
              style={{
                padding: '0.375rem 0.75rem',
                background: isEditing ? COLORS.primary : 'rgba(255,255,255,0.1)',
                color: isEditing ? '#fff' : '#94a3b8',
                border: 'none',
                borderRadius: BORDER_RADIUS.sm,
                cursor: 'pointer',
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}
            >
              {isEditing ? '✓ 完了' : '✏️ 編集'}
            </button>
          </div>
        </div>
      </div>

      {/* アクションボタン */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: SPACING.sm
      }}>
        <button
          onClick={onUsePost}
          disabled={isTyping}
          style={{
            width: '100%',
            padding: '1rem',
            background: isTyping ? 'rgba(255,255,255,0.1)' : COLORS.primary,
            color: isTyping ? '#64748b' : '#fff',
            border: 'none',
            borderRadius: BORDER_RADIUS.md,
            cursor: isTyping ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            transition: TRANSITIONS.default,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.sm
          }}
        >
          <span>🚀</span>
          この内容で投稿する
        </button>

        <button
          onClick={onRegenerate}
          style={{
            width: '100%',
            padding: '0.875rem',
            background: 'transparent',
            color: '#94a3b8',
            border: `1px solid ${COLORS.border}`,
            borderRadius: BORDER_RADIUS.md,
            cursor: 'pointer',
            fontSize: '0.9375rem',
            fontWeight: '500',
            transition: TRANSITIONS.default,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.sm
          }}
        >
          <span>🔄</span>
          作り直す
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default StepResult;
